"use client";

/**
 * Cited baseline values confirmation card.
 *
 * Extracted from `compass-intake.jsx:540-558` (`.cs-confirm` class).
 * Shows a list of label/value pairs with optional citation chips.
 */

import { CitationChip } from "../data-display/CitationChip";

export interface ConfirmValue {
  id: string;
  label: string;
  value: string;
  /** Citation number to display alongside the value. */
  cite?: number;
}

export interface ConfirmValuesProps {
  /** Values to display for confirmation. */
  values: ConfirmValue[];
  /** Index of the currently active/focused value. */
  active?: number;
  /** Called when a citation chip is clicked. */
  onCiteClick?: (n: number) => void;
}

/**
 * Baseline values card: a vertical list of labelled values with citation chips.
 *
 * `.cs-confirm` → border, rounded-container, bg-white, padded
 */
export function ConfirmValues({
  values,
  active,
  onCiteClick,
}: ConfirmValuesProps) {
  return (
    <div
      className={[
        "bg-(--bg-white) border border-(--border-subtle)",
        "rounded-(--radius-container)",
        "divide-y divide-(--border-subtle)",
        "overflow-hidden",
      ].join(" ")}
    >
      {values.map((v, i) => (
        <div
          key={v.id}
          className={[
            "flex items-center gap-3",
            "px-[14px] py-[12px]",
            "transition-colors duration-150",
            active === i ? "bg-(--bg-sky)" : "",
          ].join(" ")}
        >
          <span className="flex-1 min-w-0 text-[13px] text-(--fg-secondary)">
            {v.label}
          </span>
          <span className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[14px] font-semibold text-(--fg-primary)">
              {v.value}
            </span>
            {v.cite != null && (
              <CitationChip
                n={v.cite}
                active={active === i}
                onClick={(n) => onCiteClick?.(n)}
              />
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
